import { useEffect, useState } from "react";
import { api, imageUrl, fmtINR } from "@/lib/api";
import { Bike, MapPin, Truck, ShieldCheck, Loader2 } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1558981806-ec527fa84c39?q=80&w=1200&auto=format&fit=crop";

const CYCLE_FIELD = { day: "price_per_day", "2_weeks": "price_per_2_weeks", month: "price_per_month" };
const CYCLE_LABEL = { day: "per day", "2_weeks": "per 2 weeks", month: "per month" };

export default function MotorcycleSelector({ value, onChange }) {
  // value = { motorcycle, billing_cycle, qty } | null
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/motorcycles").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  const pick = (moto) => {
    if (value?.motorcycle?.id === moto.id) {
      onChange(null);
      return;
    }
    onChange({ motorcycle: moto, billing_cycle: "day", qty: 1 });
  };

  if (loading) return <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}><Loader2 className="w-4 h-4 animate-spin" /> Loading bikes…</div>;
  if (items.length === 0) {
    return (
      <p className="text-sm" style={{ color: "var(--text-2)" }} data-testid="moto-empty">
        No motorcycles available right now. You can still book just the stay.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="moto-grid">
      {items.map((m) => {
        const selected = value?.motorcycle?.id === m.id;
        const cycle = selected ? value.billing_cycle : "day";
        const qty = selected ? value.qty : 1;
        const unit = m[CYCLE_FIELD[cycle]];
        const cover = m.photos?.[0];
        const src = cover ? (cover.startsWith("http") ? cover : imageUrl(cover)) : FALLBACK;

        return (
          <div
            key={m.id}
            className="rounded-xl overflow-hidden transition-all cursor-pointer"
            style={{
              background: selected ? "rgba(34,60,51,0.05)" : "var(--bone)",
              border: selected ? "2px solid var(--forest)" : "1px solid var(--line)",
            }}
            onClick={() => pick(m)}
            data-testid={`moto-card-${m.id}`}
          >
            <div className="flex flex-col sm:flex-row">
              <img src={src} alt={m.name} className="w-full sm:w-44 h-40 sm:h-auto object-cover" />
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Bike className="w-4 h-4" style={{ color: "var(--terracotta)" }} />
                      <span className="font-medium text-[color:var(--text-1)]">{m.name}</span>
                      {m.age_years ? (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)" }}>
                          {m.age_years} yr
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-xs" style={{ color: "var(--text-2)" }}>
                      <MapPin className="w-3 h-3" /> Pickup: {m.pickup_location}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.can_deliver && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(198,125,99,0.12)", color: "var(--terracotta-hover)" }}>
                          <Truck className="w-3 h-3" /> Delivery available
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)" }}>
                        <ShieldCheck className="w-3 h-3" /> Insurance {fmtINR(m.insurance_amount)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-3)" }}>From</div>
                    <div className="font-serif-display text-xl text-[color:var(--forest)]">{fmtINR(m.price_per_day)}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-2)" }}>per day</div>
                  </div>
                </div>

                {selected && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--line)" }} onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-3)" }}>Billing</div>
                        <div className="flex gap-2">
                          {Object.keys(CYCLE_FIELD).map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => onChange({ ...value, billing_cycle: c })}
                              className="text-xs px-3 py-1.5 rounded-full transition-all"
                              style={{
                                background: cycle === c ? "var(--forest)" : "transparent",
                                color: cycle === c ? "#fff" : "var(--forest)",
                                border: "1px solid var(--forest)",
                              }}
                              data-testid={`moto-${m.id}-cycle-${c}`}
                            >
                              {CYCLE_LABEL[c]} · {fmtINR(m[CYCLE_FIELD[c]])}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-3)" }}>Qty</div>
                        <input
                          type="number" min={1}
                          value={qty}
                          onChange={(e) => onChange({ ...value, qty: Math.max(1, parseInt(e.target.value || "1")) })}
                          className="w-20 input-earthy !py-1.5 text-center"
                          data-testid={`moto-${m.id}-qty`}
                        />
                      </div>
                      <div className="ml-auto text-right">
                        <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-3)" }}>Bike total</div>
                        <div className="font-medium text-[color:var(--forest)]" data-testid={`moto-${m.id}-total`}>
                          {fmtINR(unit * qty)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
